import { FILES_ENDPOINT } from "Constants";
import {
  ApproveRequest,
  ApproveResponse,
  GroupUpdatesMessage,
  ProgressUpdateMessage,
} from "Types";
import { baseApi, listenToGroupUpdates } from "../../../services";
import { clearFiles } from "../store/files";
import { FileStreamQuery, ImportFileRequest, PortalFiles } from "../types";
import {
  listenToProgressUpdates,
  updateGetFilesCache,
  updateProgress,
} from "./websocketUpdates";

export const filesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFileList: builder.query<PortalFiles, FileStreamQuery>({
      query: (params) => ({
        url: `${FILES_ENDPOINT}/list`,
        params: params,
      }),
      async onCacheEntryAdded(
        arg,
        {
          updateCachedData,
          cacheDataLoaded,
          cacheEntryRemoved,
          getState,
          dispatch,
        },
      ) {
        const groupUpdatesListener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as GroupUpdatesMessage;

          if (message.entity === "filestream_file") {
            updateCachedData((draft) =>
              updateGetFilesCache(getState(), dispatch, draft, message, arg),
            );
          }
        };
        const progressUpdateListener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as ProgressUpdateMessage;

          if (message.entity === "filestream_file") {
            updateCachedData((draft) => updateProgress(draft, message));
          }
        };

        await Promise.allSettled([
          listenToProgressUpdates(
            cacheDataLoaded,
            cacheEntryRemoved,
            arg.group_id,
            progressUpdateListener,
          ),
          listenToGroupUpdates(
            cacheDataLoaded,
            cacheEntryRemoved,
            groupUpdatesListener,
            () => {
              dispatch(clearFiles());
            },
          ),
        ]);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.files.map(({ id }) => ({ type: "Files" as const, id })),
              { type: "Files", id: "LIST" },
            ]
          : [{ type: "Files", id: "LIST" }],
    }),
    deleteFile: builder.mutation<void, string>({
      query(fileId) {
        return {
          url: `${FILES_ENDPOINT}/file/${fileId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, fileId) => [
        { type: "Files", id: fileId },
      ],
    }),
    approveFile: builder.mutation<ApproveResponse, ApproveRequest>({
      query(queryArg) {
        return {
          url: `${FILES_ENDPOINT}/file/${queryArg.subjectId}/approve`,
          method: "PUT",
          body: queryArg.body,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Files", id: arg.subjectId },
      ],
    }),
    importFileFromUrl: builder.mutation<void, ImportFileRequest>({
      query: (queryArg) => ({
        url: `${FILES_ENDPOINT}/import`,
        params: { notify_on_completion: queryArg.notify_on_completion },
        body: queryArg.body,
        method: "POST",
      }),
      invalidatesTags: ["Files"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFileListQuery,
  useDeleteFileMutation,
  useApproveFileMutation,
  useImportFileFromUrlMutation,
} = filesApi;
